import { Calendar, Clock, User, MapPin, CreditCard } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    appointmentId: string;
    appointmentDate: string;
    appointmentTime?: string;
    consultationMode: string;
    doctorName: string;
    doctorSpecialty?: string;
    doctorNhcId?: string;
    clinicName?: string;
    clinicAddress?: string;
    hospitalNhcId?: string;
    status: string;
    fee?: number;
    paymentMode?: string;
    paymentStatus?: string;
    transactionId?: string;
    followUpDate?: string;
    diagnosis?: string;
    notes?: string[];
    prescriptionId?: string;
    signatories: { name: string; role: string; nhcId?: string; licenseNo?: string; institution?: string }[];
  };
}

function fmt(n: number) { return `Rs. ${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`; }

export default function AppointmentReceiptTemplate({ data }: Props) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-1.5 mb-1">
        <Calendar className="w-3.5 h-3.5 text-gray-700" />
        <span className="text-[9px] font-bold uppercase tracking-wider text-gray-700">
          {data.status === 'CANCELLED' ? 'Appointment Cancellation' : data.followUpDate ? 'Follow-up Appointment' : 'Appointment'} Receipt
        </span>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[9px]">
        <div><span className="text-gray-500">Appointment ID:</span> <span className="font-mono text-gray-800">{data.appointmentId}</span></div>
        <div><span className="text-gray-500">Date:</span> <span className="text-gray-800 font-medium">{data.appointmentDate}</span></div>
        {data.appointmentTime && <div><span className="text-gray-500">Time:</span> <span className="text-gray-800">{data.appointmentTime}</span></div>}
        <div><span className="text-gray-500">Mode:</span> <span className="text-gray-800 capitalize">{data.consultationMode.toLowerCase()}</span></div>
        <div><span className="text-gray-500">Status:</span> <span className={`font-bold ${data.status === 'CANCELLED' ? 'text-red-600' : 'text-green-700'}`}>{data.status}</span></div>
      </div>

      <div className="bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[9px]">
        <p className="text-[8px] font-bold uppercase tracking-wider text-gray-500 mb-1">Consulting Doctor</p>
        <p className="text-gray-900 font-medium">{data.doctorName}</p>
        {data.doctorSpecialty && <p className="text-gray-600 text-[8px]">{data.doctorSpecialty}</p>}
        {data.doctorNhcId && <p className="text-gray-500 font-mono text-[8px]">NHC: {data.doctorNhcId}</p>}
        {data.clinicName && <p className="text-gray-600 mt-1 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{data.clinicName}</p>}
        {data.clinicAddress && <p className="text-gray-500 text-[8px] ml-3.5">{data.clinicAddress}</p>}
      </div>

      {data.diagnosis && (
        <div className="bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[9px]">
          <p className="text-[8px] font-bold uppercase tracking-wider text-gray-500 mb-0.5">Diagnosis / Chief Complaint</p>
          <p className="text-gray-800">{data.diagnosis}</p>
        </div>
      )}

      {data.fee !== undefined && (
        <div className="bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[9px]">
          <p className="text-[8px] font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1"><CreditCard className="w-2.5 h-2.5" /> Payment</p>
          <div className="grid grid-cols-2 gap-1">
            <div><span className="text-gray-500">Fee:</span> <span className="font-mono font-bold text-gray-900">{fmt(data.fee)}</span></div>
            {data.paymentMode && <div><span className="text-gray-500">Mode:</span> <span className="text-gray-800">{data.paymentMode}</span></div>}
            {data.paymentStatus && <div><span className="text-gray-500">Status:</span> <span className="font-bold text-green-700">{data.paymentStatus}</span></div>}
            {data.transactionId && <div><span className="text-gray-500">Txn:</span> <span className="font-mono text-gray-700">{data.transactionId}</span></div>}
          </div>
        </div>
      )}

      {data.followUpDate && (
        <div className="flex items-center gap-1.5 bg-blue-50 px-3 py-1.5 border border-blue-200 rounded text-[9px] text-blue-800">
          <Clock className="w-3 h-3" />
          <span className="font-semibold">Follow-up:</span> {data.followUpDate}
        </div>
      )}

      {data.notes && data.notes.length > 0 && (
        <div className="text-[9px]">
          <p className="text-[8px] font-bold uppercase tracking-wider text-gray-500 mb-0.5">Notes</p>
          <ul className="list-disc list-inside space-y-0.5 text-gray-700 bg-gray-50 px-3 py-1.5 border border-gray-200 rounded">
            {data.notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
      )}

      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
